import React, { useState, useEffect } from "react";
import {
  Modal,
  Title,
  Button,
  Box,
  Text,
  Flex,
  ActionIcon,
  Fieldset,
  Center,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconDownload,
  IconEye,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { ReactComponent as UploadIconPlus } from "../../../assets/icon/upload_plus.svg";
import { repositorySign } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { uploadFile, getFileNames } from "../../../_base/extension/Extension";
import { EcmNodeContent } from "../../../model/EcmNodeContent";

interface AttachmentEditModalProps {
  opened: boolean;
  onClose: () => void;
  documentId: number;
  currentAttachments: string;
  onSave: () => Promise<void>;
}

interface AddAttachmentsDocumentModel {
  documentId: number;
  content: string;
}

export const AttachmentEditModal: React.FC<AttachmentEditModalProps> = ({
  opened,
  onClose,
  documentId,
  currentAttachments,
  onSave,
}) => {
  const [attachments, setAttachments] = useState<string>(currentAttachments);
  const [loading, setLoading] = useState(false);
  const [fileNameMappings, setFileNameMappings] = useState<EcmNodeContent[]>(
    []
  );

  useEffect(() => {
    if (opened && currentAttachments) {
      setAttachments(currentAttachments);
      loadFileNames(currentAttachments);
    }
  }, [opened, currentAttachments]);

  const loadFileNames = async (attachmentUrls: string) => {
    if (attachmentUrls) {
      const urls = attachmentUrls.split("||").filter((url) => url.trim());
      const fileNames = await getFileNames(urls);
      if (fileNames) {
        setFileNameMappings(fileNames);
      }
    }
  };

  const handleAddFiles = async (files: File[]) => {
    setLoading(true);
    try {
      const uploadedFiles = await uploadFile(files);
      if (uploadedFiles && uploadedFiles.length > 0) {
        const newUrls = uploadedFiles.map((file) => file.url);
        const currentUrls = attachments
          ? attachments.split("||").filter((url) => url.trim())
          : [];
        const allUrls = [...currentUrls, ...newUrls];
        setAttachments(allUrls.join("||"));

        // Reload file names
        await loadFileNames(allUrls.join("||"));

        NotificationExtension.Success("Thêm file thành công!");
      }
    } catch (error) {
      NotificationExtension.Fails("Lỗi khi thêm file!");
    }
    setLoading(false);
  };

  const handleRemoveFile = (indexToRemove: number) => {
    const urls = attachments.split("||").filter((url) => url.trim());
    const newUrls = urls.filter((_, index) => index !== indexToRemove);
    setAttachments(newUrls.join("||"));

    // Update file name mappings
    const newMappings = fileNameMappings.filter(
      (_, index) => index !== indexToRemove
    );
    setFileNameMappings(newMappings);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload: AddAttachmentsDocumentModel = {
        documentId: documentId,
        content: attachments,
      };

      const response = await repositorySign.post<MessageResponse<boolean>>(
        "/api/v1/SignPage/add-attr-sign",
        payload
      );

      if (response?.success) {
        await onSave();
        onClose();
        NotificationExtension.Success("Cập nhật file đính kèm thành công!");
      } else {
        NotificationExtension.Fails(
          response?.message || "Lỗi khi cập nhật file đính kèm!"
        );
      }
    } catch (error) {
      NotificationExtension.Fails("Lỗi khi cập nhật file đính kèm!");
    }
    setLoading(false);
  };

  const getDisplayFileName = (url: string, index: number) => {
    const originalFileName = url.split("/").pop() || "";
    const mapping = fileNameMappings.find(
      (m) => m.fileName === originalFileName
    );
    return mapping ? mapping.originName : originalFileName.slice(6);
  };

  const attachmentUrls = attachments
    ? attachments.split("||").filter((url) => url.trim())
    : [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Chỉnh sửa file đính kèm</Title>}
      size="lg"
      centered
    >
      <LoadingOverlay visible={loading} />

      <Box mb="md">
        <Fieldset legend="Thêm file mới" p={10}>
          <Dropzone onDrop={handleAddFiles} accept={[MIME_TYPES.pdf]} multiple>
            <Box style={{ textAlign: "center", padding: "20px" }}>
              <Center>
                <UploadIconPlus width={50} height={50} />
              </Center>
              <Flex align="center" justify="center" gap={5} mt="xs">
                <Text size="sm">Kéo thả File của bạn</Text>
                <Text td="underline" fw={600} c="blue" size="sm">
                  hoặc chọn tệp tin
                </Text>
              </Flex>
            </Box>
          </Dropzone>
        </Fieldset>
      </Box>

      <Box mb="md">
        <Title order={5} mb="xs">
          Danh sách file đính kèm
        </Title>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px",
                  width: "60%",
                  border: "1px solid #ebebebff",
                }}
              >
                Tên tài liệu
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "8px",
                  width: "40%",
                  border: "1px solid #ebebebff",
                }}
              >
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody>
            {attachmentUrls.length > 0 ? (
              attachmentUrls.map((url, index) => (
                <tr key={index}>
                  <td
                    style={{
                      padding: "8px",
                      border: "1px solid #ebebebff",
                    }}
                  >
                    <Text lineClamp={1}>{getDisplayFileName(url, index)}</Text>
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      border: "1px solid #ebebebff",
                    }}
                  >
                    <Flex justify="center" gap={5}>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="blue"
                        onClick={async () => {
                          try {
                            const response = await fetch(url, { mode: "cors" });
                            if (!response.ok)
                              throw new Error("Không thể tải file");
                            const blob = await response.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = blobUrl;
                            link.download = getDisplayFileName(url, index);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(
                              () => window.URL.revokeObjectURL(blobUrl),
                              1000
                            );
                          } catch (e) {
                            NotificationExtension.Fails("Không thể tải file!");
                          }
                        }}
                      >
                        <IconDownload size={14} />
                      </ActionIcon>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="green"
                        onClick={() => window.open(url, "_blank")}
                      >
                        <IconEye size={14} />
                      </ActionIcon>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="red"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Flex>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: "8px",
                    color: "gray",
                    border: "1px solid #ebebebff",
                    textAlign: "center",
                  }}
                >
                  Không có tệp tin nào được đính kèm
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      <Flex justify="flex-end" gap="md">
        <Button variant="outline" onClick={onClose}>
          Hủy
        </Button>
        <Button onClick={handleSave} loading={loading}>
          Lưu thay đổi
        </Button>
      </Flex>
    </Modal>
  );
};
